import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import * as ROUTES from '../constants/routes';
import AnimatedPage from '../components/AnimatedPage';
import Button from '../components/common/Button';
import { getRequest, postRequest } from '../config';

const IconCheck = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="#15D757" /><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const OAuthAuthorize = () => {

    const location = useLocation();
    const params = Object.fromEntries(new URLSearchParams(location['search']).entries());
    const [ui, setUi] = useState({ loading: true, deciding: false, error: null, client: null, agreed: false });

    useEffect(() => { document.title = 'Authorize | Diligentsia'; }, []);

    // Not logged in → stash the OAuth request and send the user to the home page; after they log in, Login.js brings them back here
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            localStorage.setItem('oauthPending', location['search']);
            return window.location.replace(ROUTES.HOME);
        }
        setUi(prev => ({ ...prev, loading: true, error: null, client: null, agreed: false }));
        getRequest(`/user/oauth/authorize/validate${location['search']}`, 'return-response').then((res) => {
            if (res && res['code'] == 200) setUi(prev => ({ ...prev, loading: false, client: res['body'] }));
            else setUi(prev => ({ ...prev, loading: false, error: res?.['message'] || 'Invalid authorization request' }));
        });
    }, [location['search']]);

    const decide = async (approve) => {
        setUi(prev => ({ ...prev, deciding: true }));
        const res = await postRequest('/user/oauth/authorize/decision', { ...params, approve }, true);
        if (res && res['code'] == 200 && res['body']['redirectTo']) {
            localStorage.removeItem('oauthPending');
            window.location.href = res['body']['redirectTo'];
        } else setUi(prev => ({ ...prev, deciding: false, error: res?.['message'] || 'Authorization failed' }));
    };

    const clientName = ui['client']?.['clientName'] || 'Claude';
    const scopes = ui['client']?.['scopes'] || [];

    return (
        <AnimatedPage>
            <div className="lp-page">
                <div className="lp-auth-main">
                    <div className="lp-auth-glow lp-auth-glow--tr" aria-hidden="true" />
                    <div className="lp-auth-glow lp-auth-glow--bl" aria-hidden="true" />

                    <div className="lp-auth-wrap lp-auth-wrap--narrow">
                        <div className="lp-auth-card">
                            <div className="df jcc mb1-5rem">
                                <img className="h3rem" src={require('../../images/logo.svg')} alt="Diligentsia" />
                            </div>

                            {ui['loading'] && <p className="fs0-875rem c14 tac"><i className="fa fa-spinner fa-spin mr0-5rem"></i>Checking authorization request…</p>}

                            {!ui['loading'] && ui['error'] && (
                                <>
                                    <h1 className="lp-auth-title tac">Authorization Error</h1>
                                    <p className="lp-auth-error tac mt1rem">{ui['error']}</p>
                                    <p className="fs0-875rem c14 tac mt1rem">Return to Claude and click Connect to try again.</p>
                                </>
                            )}

                            {!ui['loading'] && !ui['error'] && (
                                <>
                                    <p className="fs12px fw600 c28 ttu tac mb0-5rem">Authorization request</p>
                                    <h1 className="lp-auth-title tac">{clientName} wants to access</h1>
                                    <h1 className="lp-auth-title pink tac">your Diligentsia account</h1>
                                    <p className="fs0-875rem fw400 c14 tac mt1rem mb1-5rem">This connects {clientName} to Diligentsia so it can act on your behalf inside your account.</p>

                                    <div className="bg38 br10px px1-5rem py0-5rem mb1-5rem">
                                        {scopes.map((scope, index) => (
                                            <div key={index} className={`df aic py0-75rem ${index > 0 ? 'bss bw1px bo29' : ''}`} style={index > 0 ? { borderWidth: '1px 0 0 0' } : undefined}>
                                                <span className="df aic jcc mr1rem" style={{ flexShrink: 0 }}><IconCheck /></span>
                                                <p className="fs0-875rem fw500 c15">{scope}</p>
                                            </div>
                                        ))}
                                    </div>

                                    <label className="df aic cp mb1-5rem fs12px c14">
                                        <input type="checkbox" className="mr0-5rem" checked={ui['agreed']} onChange={(e) => setUi(prev => ({ ...prev, agreed: e['target']['checked'] }))} />
                                        <span>I agree to Diligentsia's <Link className="c3 fw500" to={ROUTES.TERMS_CONDITION} target="_blank">Terms of Service</Link> and <Link className="c3 fw500" to={ROUTES.POLICY_PRIVACY} target="_blank">Privacy Policy</Link>.</span>
                                    </label>

                                    <div className="df jcc" style={{ gap: '0.75rem' }}>
                                        <Button text="Deny" onClick={() => decide(false)} btnType="warningOutlined" disabled={ui['deciding']} />
                                        <Button text="Authorize" onClick={() => decide(true)} loading={ui['deciding']} disabled={!ui['agreed']} />
                                    </div>
                                </>
                            )}
                        </div>

                        <p className="lp-auth-trust">You're signing in to a trusted app. You can revoke access at any time from your account settings.</p>
                    </div>
                </div>
            </div>
        </AnimatedPage>
    );
};

export default OAuthAuthorize;
